import React, { useCallback, useState } from "react";

import { siteLocationPrefix, ticketingLocationPrefix } from "../../constants";
import * as styles from "./TestStyle.module.css";

const ticketingAppIdDev5 = "97b4f6f1-b513-f111-8342-6045bdd45021";
const ticketingAppIdDeploy = "a5adf1e8-1f3a-f111-88b5-7c1e525d0218";
const ticketingAppIdInt = "1634df34-693f-f111-88b5-7ced8d1955ad";

const allTicketsViewIdDev5 = "47ebc23b-c611-f111-8342-7c1e52169cc5";
const allTicketsViewIdDefault = "b0cefc9b-3b31-4ed8-82f1-5c4a5d7123a7";
const ticketPartnersViewId = "7b689d9b-18bd-4a5a-a39f-3eb4f2720770";

const getTicketingAppId = (ogcEnvURL: string) => {
  if (ogcEnvURL.includes("onegamingdeploy")) {
    return ticketingAppIdDeploy;
  }
  if (ogcEnvURL.includes("onegamingdm")) {
    return ticketingAppIdInt;
  }
  return ticketingAppIdDev5;
};

const getAllTicketsViewId = (ogcEnvURL: string) => {
  if (ogcEnvURL.includes("onegamingdeploy") || ogcEnvURL.includes("onegamingdm")) {
    return allTicketsViewIdDefault;
  }
  return allTicketsViewIdDev5;
};

const getCrmListUrl = (ogcEnvURL: string, entityName: string, viewId: string) => {
  const baseUrl = ogcEnvURL.split("?")[0];
  const appId = getTicketingAppId(ogcEnvURL);
  return `${baseUrl}?appid=${appId}&pagetype=entitylist&etn=${entityName}&viewid=${viewId}&viewType=1039`;
};

type TestTicketingActionsProps = Readonly<{
  ogcEnvURL?: string;
}>;

export const TestTicketingActions = ({ ogcEnvURL }: TestTicketingActionsProps) => {
  const [verifyUrl, setVerifyUrl] = useState<string | undefined>();
  const [isSettingUp, setIsSettingUp] = useState(false);
  const [setupError, setSetupError] = useState<string | undefined>();

  const [gdasSellerId, setGdasSellerId] = useState("");
  const [gdasLoading, setGdasLoading] = useState(false);
  const [gdasResult, setGdasResult] = useState<string | undefined>();
  const [gdasError, setGdasError] = useState<string | undefined>();

  const handleSetupVerificationCode = useCallback(async () => {
    setIsSettingUp(true);
    setSetupError(undefined);
    setVerifyUrl(undefined);
    try {
      const response = await fetch(`${siteLocationPrefix}/test/setupVerificationCode`);
      if (!response.ok) {
        throw new Error(`Failed: ${response.status}`);
      }
      const data: { verificationCode: string } = await response.json();
      const url = `${window.location.origin}${ticketingLocationPrefix}/verify-notification-email?id=${data.verificationCode}`;
      setVerifyUrl(url);
    } catch (e) {
      setSetupError((e as Error).message);
    } finally {
      setIsSettingUp(false);
    }
  }, []);

  const handleTestGdasClaims = useCallback(async () => {
    if (!gdasSellerId.trim()) {
      return;
    }
    setGdasLoading(true);
    setGdasResult(undefined);
    setGdasError(undefined);
    try {
      const response = await fetch(
        `${siteLocationPrefix}/test/gdasClaims?sellerId=${encodeURIComponent(gdasSellerId.trim())}`
      );
      const data: unknown = await response.json();
      setGdasResult(JSON.stringify(data, null, 2));
    } catch (e) {
      setGdasError((e as Error).message);
    } finally {
      setGdasLoading(false);
    }
  }, [gdasSellerId]);

  return (
    <div className={styles.ticketingActionsWrapper}>
      <h3>Ticketing Actions</h3>
      <div>
        <form action={`${ticketingLocationPrefix}/cacherefresh`} method="GET" target="_blank">
          Refresh Ticket Partner Cache{" "}
          <button className={styles.testButton} type="submit">
            refresh
          </button>
        </form>
      </div>
      <div>
        Setup Verification Code{" "}
        <button
          className={styles.testButton}
          type="button"
          onClick={handleSetupVerificationCode}
          disabled={isSettingUp}
        >
          {isSettingUp ? "Setting up..." : "generate"}
        </button>
        {setupError && <span style={{ color: "red", marginLeft: "0.5rem" }}>{setupError}</span>}
        {verifyUrl && (
          <div style={{ marginTop: "0.5rem", wordBreak: "break-all" }}>
            <strong>Verify URL:</strong>{" "}
            <a href={verifyUrl} target="_blank" rel="noopener noreferrer">
              {verifyUrl}
            </a>
          </div>
        )}
      </div>
      <div>
        Test GDAS Claims{" "}
        <input
          type="text"
          placeholder="Seller ID"
          value={gdasSellerId}
          onChange={e => setGdasSellerId(e.target.value)}
          style={{ width: "120px", marginRight: "0.5rem" }}
        />
        <button
          className={styles.testButton}
          type="button"
          onClick={handleTestGdasClaims}
          disabled={gdasLoading || !gdasSellerId.trim()}
        >
          {gdasLoading ? "Loading..." : "test"}
        </button>
        {gdasError && <span style={{ color: "red", marginLeft: "0.5rem" }}>{gdasError}</span>}
        {gdasResult && (
          <pre
            style={{
              marginTop: "0.5rem",
              padding: "0.5rem",
              background: "#1e1e1e",
              color: "#d4d4d4",
              borderRadius: "4px",
              fontSize: "12px",
              overflow: "auto",
              maxHeight: "300px",
              whiteSpace: "pre-wrap",
              wordBreak: "break-all",
            }}
          >
            {gdasResult}
          </pre>
        )}
      </div>
      {ogcEnvURL && (
        <>
          <h3>CRM Pages</h3>
          <div>
            <a
              href={getCrmListUrl(ogcEnvURL, "ticket_ticket", getAllTicketsViewId(ogcEnvURL))}
              target="_blank"
              rel="noopener noreferrer"
            >
              <button className={styles.testButton} type="button">
                All Tickets
              </button>
            </a>
          </div>
          <div>
            <a
              href={getCrmListUrl(ogcEnvURL, "ticket_ticketpartner", ticketPartnersViewId)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <button className={styles.testButton} type="button">
                Ticket Partners
              </button>
            </a>
          </div>
        </>
      )}
    </div>
  );
};
